import { motion } from "framer-motion";
import { GradientButton } from "@/components/ui/gradient-button";
import { Check, Circle } from "lucide-react";
import { AceenaSymbolShape } from "@/components/ui/aceena-symbol-shape";

export const HeroSection = () => {
  const fadeUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        delay: 0.5 + i * 0.2,
        ease: [0.25, 0.4, 0.25, 1] as const,
      },
    }),
  };

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-white">
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#7EF5E5]/[0.05] via-transparent to-[#122649]/[0.05] blur-3xl" />

      {/* Animated Aceena symbol shapes */}
      <div className="absolute inset-0 overflow-hidden">
        <AceenaSymbolShape
          delay={0.3}
          size={520}
          rotate={12}
          variant="mint"
          className="left-[-8%] md:left-[-3%] top-[12%] md:top-[18%]"
        />

        <AceenaSymbolShape
          delay={0.5}
          size={460}
          rotate={-18}
          variant="navy"
          className="right-[-6%] md:right-[0%] top-[65%] md:top-[72%]"
        />

        <AceenaSymbolShape
          delay={0.4}
          size={320}
          rotate={-8}
          variant="mint"
          className="left-[6%] md:left-[10%] bottom-[4%] md:bottom-[10%]"
        />

        <AceenaSymbolShape
          delay={0.6}
          size={260}
          rotate={22}
          variant="navy"
          className="right-[18%] md:right-[22%] top-[8%] md:top-[14%]"
        />

        <AceenaSymbolShape
          delay={0.7}
          size={220}
          rotate={-24}
          variant="mint"
          className="left-[22%] md:left-[26%] top-[4%] md:top-[9%]"
        />
      </div>

      <div className="container relative z-10 py-20 lg:py-32">
        <div className="max-w-3xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            custom={0}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7EF5E5]/[0.08] border border-[#7EF5E5]/[0.15] mb-8 md:mb-12"
          >
            <Circle className="h-2 w-2 fill-[#7EF5E5] text-[#7EF5E5]" />
            <span className="text-sm text-[#122649]/70 tracking-wide font-medium">
              Consultoria técnica especializada
            </span>
          </motion.div>

          {/* H1 - Main Headline */}
          <motion.div
            custom={1}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-6 md:mb-8 tracking-tight">
              <span className="text-[#122649]">
                Antes de pedir propostas direto às locadoras,
              </span>
              <br />
              <span className="font-extrabold bg-gradient-to-r from-[#122649] via-[#84EFDF] to-[#122649] bg-[length:200%_auto] animate-gradient-x bg-clip-text text-transparent">
                fale com a Aceena.
              </span>
            </h1>
          </motion.div>

          {/* Subheadline */}
          <motion.div
            custom={2}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
          >
            <p className="text-lg sm:text-xl text-[#122649]/70 max-w-2xl mx-auto mb-4 leading-relaxed font-light">
              A consultoria técnica em carro por assinatura para quem quer fazer
              a escolha certa — sem depender só de preço.
            </p>
          </motion.div>

          {/* Support line */}
          <motion.div
            custom={3}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
          >
            <p className="text-sm text-[#122649]/50 max-w-xl mx-auto mb-10 leading-relaxed">
              Com +30 anos de experiência no setor automotivo e consultoria
              imparcial, ajudamos você a entender coberturas, serviços e
              condições de contrato antes de fechar com qualquer locadora.
            </p>
          </motion.div>

          {/* CTAs */}
          <motion.div
            custom={4}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10"
          >
            <GradientButton
              variant="hero"
              size="lg"
              onClick={() => {
                if (typeof window !== 'undefined' && (window as any).fbq) {
                  (window as any).fbq('track', 'Lead');
                }
                window.open("https://wa.me/5511989004165?text=Olá! Gostaria de falar com um especialista sobre carro por assinatura.", "_blank");
              }}
            >
              Falar com um especialista
            </GradientButton>
          </motion.div>

          {/* Trust Row */}
          <motion.div
            custom={5}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-wrap items-center justify-center gap-6 text-sm text-[#122649]/60"
          >
            {[
              "Consultoria técnica imparcial",
              "Foco em coberturas, serviços e contratos",
              "Sem custo adicional para você",
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#7EF5E5]" />
                <span>{item}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </section>
  );
};
